import { raygun } from '@ynap/clientside-monitoring'

import { getReturnItemsValue } from '../utils/helpers'
import {
    getPlannedPickupDateAndTime as getPlannedPickupDateAndTimeDHL,
    formatPlannedPickupDateAndTime,
} from '../utils/DHLDateTimeUtils'
import { UPSPickupMessaging, DHLPickupMessaging } from '../utils/messagingUtils'
import { getPlannedPickupDateAndTime as getPlannedPickupDateAndTimeUPS } from '../utils/UPSDateTimeUtils'
import { DateObject, getToday, getTomorrow, getCurrentDateTimePlus15Mins, dateToObject } from '../utils/dateTimeUtils'

import type { ICourierConfig } from '../types/config'
import type { IShippingAddress } from '../types/index'
import type { IDHLCommon } from '../types/Courier/DHL/DHLCommon'
import type { IUPSCommon } from '../types/Courier/UPS/UPSCommon'
import type { IPickUpDetails } from '../types/Courier/DHL/DHLPickUpDetails'
import type { DHLRatesResponse } from '../types/Courier/DHL/DHLRatesResponse'
import type { IGetPickupAvailability } from '../types/IGetPickupAvailability'
import type { UPSTimeInTransitResponse } from '../types/Courier/UPS/UPSTimeInTransitResponse'

const reportToRaygun = (message, { requestBody, error }) =>
    raygun.helpers.agent('send', {
        error: new Error(message),
        tags: [
            requestBody.courier,
            'book-a-return',
            `${requestBody.courier}-pickup-availability-error`,
            requestBody.country,
        ],
        customData: {
            requestBody,
            errorString: error.toString(),
        },
    })

const getPickupAvailability = (
    config: any,
    messages: any,
    shippingAddress: IShippingAddress,
    userDate: Date | undefined,
    returnItems: any[],
    returnCourier: string
): Promise<IGetPickupAvailability> => {
    const clientId = config.global?.services.commerce.clientId
    const { collectionService } = config.head
    const { couriers } = collectionService
    const courierConfig: ICourierConfig = couriers[returnCourier]

    const dateToday: DateObject = getToday()
    const dateTomorrow: DateObject = getTomorrow()
    const dateSelected = userDate ? dateToObject(userDate) : dateToday

    if (returnCourier === 'DHL') {
        return fetchDHLAvailability(shippingAddress, courierConfig, dateSelected, messages, clientId)
            .then((availability) => {
                if (availability.missedCutOff) {
                    return fetchDHLAvailability(shippingAddress, courierConfig, dateTomorrow, messages, clientId)
                }
                return availability
            })
            .catch(() => ({ error: true })) // sends error to the component
    }
    // if returnCourier === "UPS"
    return fetchUPSAvailability(shippingAddress, courierConfig, dateSelected, messages, returnItems, clientId)
        .then((availability) => {
            if (availability.missedCutOff) {
                return fetchUPSAvailability(
                    shippingAddress,
                    courierConfig,
                    dateTomorrow,
                    messages,
                    returnItems,
                    clientId
                )
            }
            return availability
        })
        .catch(() => ({ error: true })) // sends error to the component
}

export default getPickupAvailability

//fetches
const fetchDHLAvailability = (
    shippingAddress: IShippingAddress,
    config: ICourierConfig,
    date: DateObject,
    messages: any,
    clientId: string
): Promise<IDHLCommon> => {
    const { dimensions, baseUrl } = config
    const { weight, width, height, length, unitOfMeasurement } = dimensions || {}
    const { city, country, zipCode } = shippingAddress

    const plannedShippingDate = `${date.year}-${date.month}-${date.day}`

    const nextBusinessDay = true

    const originPostalCode = zipCode === '' ? '0000' : zipCode

    let jsonResponse
    const pickupInfo = fetch(
        `${baseUrl}/rates?originCountryCode=${country}&originPostalCode=${originPostalCode}&originCityName=${city}&weight=${weight}&length=${length}&width=${width}&height=${height}&plannedShippingDate=${plannedShippingDate}&unitOfMeasurement=${unitOfMeasurement}&nextBusinessDay=${nextBusinessDay}`,
        {
            headers: {
                'Content-Type': 'application/json',
                'x-ibm-client-id': clientId,
            },
        }
    )
        .then(async (res) => {
            if (!res.ok) {
                const error = await res.text()
                throw new Error(error)
            }
            return res.json()
        })
        .then((json: DHLRatesResponse) => {
            jsonResponse = json
            return mapDHLToCommon(json, messages, date)
        })
        .catch((e) => {
            reportToRaygun(`Unsucessful response when hitting ${baseUrl}/rates`, {
                requestBody: {
                    courier: 'DHL',
                    country: country,
                    originPostalCode: originPostalCode,
                    city: city,
                    plannedShippingDate,
                    hasProducts: !!jsonResponse?.products,
                    productLength: jsonResponse?.products ? jsonResponse?.products?.length : 0,
                },
                error: e,
            })
            throw new Error(e)
        })

    return pickupInfo
}

const fetchUPSAvailability = (
    shippingAddress: IShippingAddress,
    config: ICourierConfig,
    date: DateObject,
    messages: any,
    returnItems: any[],
    clientId: string
): Promise<IUPSCommon> => {
    const { baseUrl } = config
    const { city, state, country, zipCode } = shippingAddress

    const requestDateFormatted = `${date.year}${date.month}${date.day}`

    const valueOfGoods = getReturnItemsValue(returnItems)
    const currencyOfGoods = returnItems[0].price.currency.label

    const body = {
        TimeInTransitRequest: {
            ShipFrom: {
                Address: {
                    City: city,
                    StateProvinceCode: state?.length >= 2 ? state : '', // UPS only allow state code (e.g. NJ) or province (for Ireland)
                    CountryCode: country,
                    PostalCode: zipCode,
                },
            },
            Pickup: {
                Date: requestDateFormatted, //20210126
            },
            InvoiceLineTotal: {
                MonetaryValue: valueOfGoods.toString(),
                CurrencyCode: currencyOfGoods,
            },
        },
    }

    let jsonResponse

    const pickupInfo = fetch(`${baseUrl}/availability`, {
        method: 'POST',
        headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
            'x-ibm-client-id': clientId,
        },
        body: JSON.stringify(body),
    })
        .then(async (res) => {
            if (!res.ok) {
                const error = await res.text()
                throw new Error(error)
            }
            return res.json()
        })
        .then((json: UPSTimeInTransitResponse) => {
            jsonResponse = json
            return mapUPSToCommon(json, shippingAddress, messages, date)
        })
        .catch((e) => {
            reportToRaygun(`Unsucessful response when hitting ${baseUrl}/lion/availability`, {
                requestBody: {
                    courier: 'UPS',
                    country: country,
                    requestBody: body,
                    hasProducts: !!jsonResponse?.products,
                    productLength: jsonResponse?.products ? jsonResponse?.products?.length : 0,
                },
                error: e,
            })
            throw new Error(e)
        })

    return pickupInfo
}

//mappers
const mapDHLToCommon = (data: DHLRatesResponse, messages: any, dateRequested: DateObject): IDHLCommon => {
    const currentDateTime: Date = getCurrentDateTimePlus15Mins()

    const { missedCutOff, plannedPickupDateAndTime, pickupLatest, productCode }: IPickUpDetails =
        getPlannedPickupDateAndTimeDHL(data, currentDateTime)

    const pickupMessages = new DHLPickupMessaging(messages, pickupLatest, currentDateTime, plannedPickupDateAndTime)
    const timePeriod = pickupMessages.formatTimePeriod()
    const datePeriod = pickupMessages.formatDatePeriod()

    const formattedPlannedPickupDateAndTime = formatPlannedPickupDateAndTime(plannedPickupDateAndTime)

    const isPlannedPickupRequestedDate =
        dateRequested.day === String(plannedPickupDateAndTime.getDate()).padStart(2, '0')
    return {
        timePeriod,
        datePeriod,
        dateRequested: new Date(
            dateRequested.year,
            Number(dateRequested.month) - 1,
            Number(dateRequested.day),
            Number(dateRequested.hour),
            Number(dateRequested.minutes)
        ),
        missedCutOff,
        plannedPickupDateAndTime: formattedPlannedPickupDateAndTime,
        productCode,
        isPlannedPickupRequestedDate,
    }
}

const mapUPSToCommon = (
    data: UPSTimeInTransitResponse,
    shippingAddress: IShippingAddress,
    messages: any,
    dateRequested: DateObject
): IUPSCommon => {
    const currentDateTime: Date = getCurrentDateTimePlus15Mins()
    const { year, month, day, hour, minutes } = dateToObject(currentDateTime)
    const currentTime = `${hour}${minutes}`
    const currentDate = `${year}${month}${day}`

    const { missedCutOff, plannedPickupDateAndTime, isPickupEarliestNow, productCode } = getPlannedPickupDateAndTimeUPS(
        data,
        shippingAddress,
        currentDate,
        currentTime
    )

    const pickupMessages = new UPSPickupMessaging(messages, isPickupEarliestNow, currentDate, plannedPickupDateAndTime)
    const timePeriod = pickupMessages.formatTimePeriod()
    const datePeriod = pickupMessages.formatDatePeriod()

    const requestedDate = `${dateRequested.year}${dateRequested.month}${dateRequested.day}`
    const isPlannedPickupRequestedDate = requestedDate === plannedPickupDateAndTime.PickupDate

    return {
        timePeriod,
        datePeriod,
        dateRequested: new Date(
            dateRequested.year,
            Number(dateRequested.month) - 1,
            Number(dateRequested.day),
            Number(dateRequested.hour),
            Number(dateRequested.minutes)
        ),
        missedCutOff,
        productCode,
        plannedPickupDateAndTime,
        isPlannedPickupRequestedDate,
    }
}
